/**
 * Builds a rotated drawing surface so we always crop from an upright canvas.
 * The cropping UI stores coordinates relative to the original orientation, so
 * we rotate a temporary canvas when the user applies a rotation.
 */
const createSourceCanvas = (image, rotation, naturalWidth, naturalHeight) => {
  if (rotation === 0) {
    return {
      canvas: image,
      width: naturalWidth,
      height: naturalHeight,
    }
  }

  const rotatedCanvas = document.createElement('canvas')
  const rotatedCtx = rotatedCanvas.getContext('2d')

  if (!rotatedCtx) {
    throw new Error('Unable to prepare rotation canvas')
  }

  if (rotation === 90 || rotation === 270) {
    rotatedCanvas.width = naturalHeight
    rotatedCanvas.height = naturalWidth
  } else {
    rotatedCanvas.width = naturalWidth
    rotatedCanvas.height = naturalHeight
  }

  rotatedCtx.imageSmoothingEnabled = true
  rotatedCtx.imageSmoothingQuality = 'high'

  rotatedCtx.translate(rotatedCanvas.width / 2, rotatedCanvas.height / 2)
  rotatedCtx.rotate((rotation * Math.PI) / 180)
  rotatedCtx.translate(-naturalWidth / 2, -naturalHeight / 2)
  rotatedCtx.drawImage(image, 0, 0)

  return {
    canvas: rotatedCanvas,
    width: rotatedCanvas.width,
    height: rotatedCanvas.height,
  }
}

/**
 * Converts relative crop area (fractions of width/height) into absolute pixel coordinates after rotation.
 */
const convertRelativeCropToPixels = ({ x, y, w, h }, rotation, sourceWidth, sourceHeight) => {
  let pixelX
  let pixelY
  let pixelWidth
  let pixelHeight

  if (rotation === 90) {
    pixelX = (1 - y - h) * sourceWidth
    pixelY = x * sourceHeight
    pixelWidth = h * sourceWidth
    pixelHeight = w * sourceHeight
  } else if (rotation === 180) {
    pixelX = (1 - x - w) * sourceWidth
    pixelY = (1 - y - h) * sourceHeight
    pixelWidth = w * sourceWidth
    pixelHeight = h * sourceHeight
  } else if (rotation === 270) {
    pixelX = y * sourceWidth
    pixelY = (1 - x - w) * sourceHeight
    pixelWidth = h * sourceWidth
    pixelHeight = w * sourceHeight
  } else {
    pixelX = x * sourceWidth
    pixelY = y * sourceHeight
    pixelWidth = w * sourceWidth
    pixelHeight = h * sourceHeight
  }

  return {
    x: Math.round(pixelX),
    y: Math.round(pixelY),
    width: Math.round(pixelWidth),
    height: Math.round(pixelHeight),
  }
}

/**
 * Draws the requested crop to a standalone canvas so we can export the result.
 * This keeps all smoothing options in one place and gives us a single spot to
 * surface export errors.
 */
const renderCropToDataUrl = (sourceCanvas, { x, y, width, height }) => {
  // Validate crop dimensions
  if (width <= 0 || height <= 0) {
    throw new Error('Crop dimensions must be positive')
  }

  // Create output canvas for the cropped image
  const outputCanvas = document.createElement('canvas')
  outputCanvas.width = width
  outputCanvas.height = height

  // Get 2D drawing context
  const drawingContext = outputCanvas.getContext('2d')
  if (!drawingContext) {
    throw new Error('Unable to prepare output canvas')
  }

  const sourceImageMeasurements = {
    x,
    y,
    width,
    height,
  }

  const finalImageMeasurements = {
    x: 0,
    y: 0,
    width,
    height,
  }

  drawingContext.drawImage(
    sourceCanvas,
    sourceImageMeasurements.x,
    sourceImageMeasurements.y,
    sourceImageMeasurements.width,
    sourceImageMeasurements.height,
    finalImageMeasurements.x,
    finalImageMeasurements.y,
    finalImageMeasurements.width,
    finalImageMeasurements.height
  )

  const pngDataUrl = outputCanvas.toDataURL('image/png')
  if (!pngDataUrl || pngDataUrl === 'data:,') {
    throw new Error('Unable to export cropped image')
  }

  return pngDataUrl
}

/* x, y, w, h and rotation are all relative and must be crop data from the portrait selector component */
const createCroppedImage = ({ src, x, y, w, h, rotation = 0 } = {}) => {
  return new Promise((resolve, reject) => {
    let hasResolved = false
    let loadTimeout
    let fallbackCheck
    let attemptedCacheBypass = false
    let objectUrl

    const clearTimers = () => {
      if (loadTimeout) clearTimeout(loadTimeout)
      if (fallbackCheck) clearTimeout(fallbackCheck)
    }

    const revokeObjectUrl = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
        objectUrl = null
      }
    }

    const finalizeError = (message, error) => {
      if (hasResolved) return
      hasResolved = true
      clearTimers()
      revokeObjectUrl()
      if (error) {
        console.error('Failed to create cropped image:', error)
      }
      const finalError = new Error(message)

      if (error instanceof Error) {
        finalError.cause = error
      } else if (error !== undefined) {
        finalError.cause = new Error(String(error))
      }

      reject(finalError)
    }

    const finalizeSuccess = (value) => {
      if (hasResolved) return
      hasResolved = true
      clearTimers()
      revokeObjectUrl()
      resolve(value)
    }

    // Validate input parameters
    if (!src) {
      finalizeError('Image source URL is required')
      return
    }

    // Rewrite external image URLs to relative paths
    // This ensures images load from the same origin as the app, avoiding CORS issues
    // Only rewrite URLs from trusted FamilySearch domains (e.g., sg3lb0.familysearch.org)
    // to prevent malicious URLs from being rewritten
    let rewrittenSrc = src
    if (src.startsWith('https://') && /\.familysearch\.org\/service\/records/.test(src)) {
      rewrittenSrc = src.replace(/^https?:\/\/[^/]+/, '')
    }

    const image = new Image()

    // Detect if this is a proxied/relative URL (like /service/records/...)
    // or an absolute URL that needs CORS handling
    const isRelativeUrl = rewrittenSrc.startsWith('/')
    const isSameOrigin = isRelativeUrl || new URL(rewrittenSrc, window.location.href).origin === window.location.origin

    if (!isSameOrigin) {
      image.crossOrigin = 'anonymous'
    }

    loadTimeout = setTimeout(() => {
      finalizeError('Image loading timed out')
    }, 5000)

    const processImage = () => {
      try {
        const { naturalWidth, naturalHeight } = image

        if (!naturalWidth || !naturalHeight) {
          finalizeError('Image did not provide usable dimensions')
          return
        }

        const {
          canvas: sourceCanvas,
          width: sourceWidth,
          height: sourceHeight,
        } = createSourceCanvas(image, rotation, naturalWidth, naturalHeight)

        const cropPixels = convertRelativeCropToPixels({ x, y, w, h }, rotation, sourceWidth, sourceHeight)
        const pngDataUrl = renderCropToDataUrl(sourceCanvas, cropPixels)

        finalizeSuccess(pngDataUrl)
      } catch (error) {
        finalizeError(error?.message || 'Failed to process image', error)
      }
    }

    // Enhanced load detection
    const checkImageLoaded = () => {
      if (hasResolved) return

      // Multiple checks for successful loading
      const isLoaded = image.complete && image.naturalWidth > 0 && image.naturalHeight > 0

      if (isLoaded) {
        processImage()
      } else if (image.complete) {
        // Image is complete but has zero dimensions - likely CORS issue

        finalizeError('Image completed without usable pixel data')
      }
    }

    const attemptCacheBypass = async () => {
      if (hasResolved) return

      if (typeof fetch !== 'function') {
        finalizeError('Image failed to load')
        return
      }

      try {
        const response = await fetch(rewrittenSrc, {
          mode: isSameOrigin ? 'same-origin' : 'cors',
          credentials: isSameOrigin ? 'include' : 'omit',
          cache: 'reload',
        })

        if (!response.ok) {
          throw new Error(`Unexpected response status: ${response.status}`)
        }

        const blob = await response.blob()
        revokeObjectUrl()
        objectUrl = URL.createObjectURL(blob)

        // object URLs are same-origin, so clear the crossOrigin hint
        image.crossOrigin = null

        // Give the new request the same amount of time to load
        if (loadTimeout) {
          clearTimeout(loadTimeout)
        }
        loadTimeout = setTimeout(() => {
          finalizeError('Image loading timed out')
        }, 5000)

        image.src = objectUrl
      } catch (error) {
        finalizeError('Image failed to load', error)
      }
    }

    // Enhanced event handlers
    image.onload = () => {
      // Use setTimeout to ensure image is fully processed
      setTimeout(checkImageLoaded, 10)
    }

    image.onerror = () => {
      if (hasResolved) return

      if (!attemptedCacheBypass) {
        attemptedCacheBypass = true
        attemptCacheBypass()
        return
      }

      finalizeError('Image failed to load')
    }

    // Additional fallback check for problematic images
    fallbackCheck = setTimeout(() => {
      if (!hasResolved && image.complete) {
        checkImageLoaded()
      }
    }, 2000)

    // Set source and start loading
    try {
      image.src = rewrittenSrc

      // If image is already cached and complete, check immediately
      if (image.complete) {
        setTimeout(checkImageLoaded, 0)
      }
    } catch (error) {
      finalizeError('Invalid image URL', error)
    }
  })
}

export default createCroppedImage
